import { z } from 'zod';

import { DayOfWeek } from '../../../../../../types/day-of-week';
import { parseTimeToMinuteOfTheDay } from '../helpers/parse-work-schedule';

/**
 * Time format validation regex (e.g., "23:34", "08:15")
 */
const timeFormatRegex = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/**
 * Schema for a single work day
 */
const workDaySchema = (t: (key: string, options?: any) => string) =>
  z
    .object({
      enabled: z.boolean().default(false),
      startTime: z
        .string()
        .regex(timeFormatRegex, {
          message: t('authenticated.organization:workingHours.validation.startTime'),
        })
        .nullable()
        .optional(),
      endTime: z
        .string()
        .regex(timeFormatRegex, {
          message: t('authenticated.organization:workingHours.validation.endTime'),
        })
        .nullable()
        .optional(),
    })
    .refine(
      data => {
        if (!data.enabled) return true;
        if (!data.startTime || !data.endTime) return true;
        const start = parseTimeToMinuteOfTheDay(data.startTime);
        const end = parseTimeToMinuteOfTheDay(data.endTime);
        return end > start;
      },
      {
        message: t('authenticated.organization:workingHours.validation.endTimeAfterStartTime'),
        path: ['endTime'],
      }
    );

/**
 * Schema for work schedule
 */
const workScheduleSchema = (t: (key: string, options?: any) => string) =>
  z.object(
    Object.fromEntries(
      Object.values(DayOfWeek).map(day => [day, workDaySchema(t).default({ enabled: false })])
    )
  );

/**
 * Default work schedule configuration
 */
const defaultWorkSchedule = Object.fromEntries(
  Object.values(DayOfWeek).map(day => {
    const isWeekday = day !== DayOfWeek.SATURDAY && day !== DayOfWeek.SUNDAY;
    return [
      day,
      isWeekday ? { enabled: true, startTime: '9:00', endTime: '17:00' } : { enabled: false },
    ];
  })
);

/**
 * Creates a Zod schema for organization form validation
 * @param t Translation function
 * @returns Zod schema for organization form
 */
export const createOrganizationFormSchema = (t: (key: string, options?: any) => string) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, {
        message: t('authenticated.organization:validation.name.required'),
      })
      .min(2, {
        message: t('authenticated.organization:validation.name.min', { min: 2 }),
      }),
    industry: z.string().nullable(),
    country: z.string().nullable(),
    timezone: z.string().nullable(),
    defaultCurrencyCode: z.string().nullable(),
    services: z.array(z.string()).nullable(),
    workSchedule: workScheduleSchema(t).default(defaultWorkSchedule),
  });

export type OrganizationFormValues = z.infer<ReturnType<typeof createOrganizationFormSchema>>;
